import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "../../ui/tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import {
  Folder,
  FolderOpen,
  Star,
  Grid3x3,
  Play,
  Plus,
  MoreVertical,
  Edit2,
  Trash2,
  ChevronsLeft,
  ChevronsRight,
  PenLine,
  BookOpen,
} from "lucide-react";
import { useDirection } from "../../../hooks/useDirection";
import { cn } from "../../../lib/utils";

interface FolderSidebarProps {
  selectedFolder: string | null;
  onFolderSelect: (folderId: string | null) => void;
  folders: Array<{
    _id: string;
    name: string;
    color: string;
    count?: number;
  }>;
  collections?: Array<{
    _id: string;
    name: string;
    level: number;
    organization?: { name: string };
    workflows: any[];
    workflowCount: number;
  }>;
  onCreateFolder: (name: string, color: string) => void;
  onUpdateFolder: (folderId: string, name: string, color: string) => void;
  onDeleteFolder: (folderId: string) => void;
  totalCount: number;
  favoritesCount: number;
  unorganizedCount: number;
  inProgressCount?: number;
  createdCount?: number;
  libraryCount?: number;
  isMobile?: boolean;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  /** When true, renders without its own width/border/toggle (used inside ZoneSidebar portal) */
  embedded?: boolean;
}

const FOLDER_COLORS = [
  { name: "Red", hex: "#EF4444" },
  { name: "Orange", hex: "#F97316" },
  { name: "Yellow", hex: "#EAB308" },
  { name: "Green", hex: "#22C55E" },
  { name: "Blue", hex: "#3B82F6" },
  { name: "Purple", hex: "#A855F7" },
  { name: "Pink", hex: "#EC4899" },
  { name: "Gray", hex: "#6B7280" },
];

const FolderSidebar: React.FC<FolderSidebarProps> = ({
  selectedFolder,
  onFolderSelect,
  folders,
  collections = [],
  onCreateFolder,
  onUpdateFolder,
  onDeleteFolder,
  totalCount,
  favoritesCount,
  unorganizedCount,
  inProgressCount = 0,
  createdCount,
  libraryCount,
  isMobile = false,
  collapsed = false,
  onToggleCollapsed,
  embedded = false,
}) => {
  const { t } = useTranslation();
  const { isRTL } = useDirection();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingFolder, setEditingFolder] = useState<any>(null);
  const [folderName, setFolderName] = useState("");
  const [selectedColor, setSelectedColor] = useState(FOLDER_COLORS[4].hex);
  const [customColor, setCustomColor] = useState("");
  const [showCustomColorPicker, setShowCustomColorPicker] = useState(false);

  const isCollapsed = collapsed && !isMobile;

  const handleCreateFolder = () => {
    if (folderName.trim()) {
      onCreateFolder(folderName.trim(), selectedColor);
      setFolderName("");
      setSelectedColor(FOLDER_COLORS[4].hex);
      setCustomColor("");
      setShowCustomColorPicker(false);
      setShowCreateDialog(false);
    }
  };

  const handleEditFolder = () => {
    if (folderName.trim() && editingFolder) {
      onUpdateFolder(editingFolder._id, folderName.trim(), selectedColor);
      setShowEditDialog(false);
      setEditingFolder(null);
      setFolderName("");
    }
  };

  const openEditDialog = (folder: any) => {
    setEditingFolder(folder);
    setFolderName(folder.name);
    setSelectedColor(folder.color);
    // Check if the color is a custom color (not in predefined list)
    const isPredefinedColor = FOLDER_COLORS.some(c => c.hex === folder.color);
    if (!isPredefinedColor) {
      setCustomColor(folder.color);
      setShowCustomColorPicker(true);
    } else {
      setCustomColor("");
      setShowCustomColorPicker(false);
    }
    setShowEditDialog(true);
  };

  const handleCustomColorChange = (color: string) => {
    setCustomColor(color);
    setSelectedColor(color);
  };

  const tooltipSide = isRTL ? "left" as const : "right" as const;

  // Helper to render a nav item (collapsed vs expanded)
  const NavItem = ({
    id,
    icon,
    label,
    count,
    countClassName,
    activeClass = "bg-primary/10 text-primary",
    inactiveClass = "hover:bg-gray-100 text-gray-600",
  }: {
    id: string | null;
    icon: React.ReactNode;
    label: string;
    count?: number | null;
    countClassName?: string;
    activeClass?: string;
    inactiveClass?: string;
  }) => {
    const isActive = selectedFolder === id;
    if (isCollapsed) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => onFolderSelect(id)}
              className={cn(
                "w-full flex items-center justify-center h-9 rounded-lg transition-colors duration-150",
                isActive ? activeClass : inactiveClass
              )}
            >
              {icon}
            </button>
          </TooltipTrigger>
          <TooltipContent side={tooltipSide}>
            {label}{count != null ? ` (${count})` : ""}
          </TooltipContent>
        </Tooltip>
      );
    }
    return (
      <button
        onClick={() => onFolderSelect(id)}
        className={cn(
          "w-full flex items-center gap-2.5 px-3 py-1.5 rounded-lg transition-colors duration-150 text-sm",
          isActive ? `${activeClass} font-medium` : inactiveClass
        )}
      >
        <span className="shrink-0">{icon}</span>
        <span className="flex-1 text-start truncate">{label}</span>
        {count != null && (
          <span className={cn("text-xs tabular-nums", countClassName ?? "text-gray-400")}>
            {count}
          </span>
        )}
      </button>
    );
  };

  // Section label shown only in expanded mode
  const SectionLabel = ({ label }: { label: string }) =>
    isCollapsed ? null : (
      <div className="px-3 pt-4 pb-1">
        <span className="text-[10px] font-semibold tracking-widest uppercase text-gray-400 select-none">
          {label}
        </span>
      </div>
    );

  // Collapsed-mode divider
  const CollapsedDivider = () =>
    isCollapsed ? <div className="h-px bg-gray-100 mx-3 my-2" /> : null;

  return (
    <div className={cn(
      "relative group/sidebar",
      embedded
        ? "w-full h-full"
        : cn(
            isMobile ? "w-full" : isCollapsed ? "w-[52px]" : "w-60",
            "transition-[width] duration-200 ease-linear"
          )
    )}>
      {/* Toggle button (desktop only, not in embedded mode) */}
      {!isMobile && !embedded && onToggleCollapsed && (
        <button
          onClick={onToggleCollapsed}
          className={cn(
            "absolute top-20 z-10 w-6 h-6 rounded-full border border-gray-200 bg-white shadow-sm flex items-center justify-center",
            "text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-all",
            "opacity-0 group-hover/sidebar:opacity-100",
            isRTL ? "-left-3" : "-right-3"
          )}
        >
          {isCollapsed
            ? (isRTL ? <ChevronsLeft className="w-3 h-3" /> : <ChevronsRight className="w-3 h-3" />)
            : (isRTL ? <ChevronsRight className="w-3 h-3" /> : <ChevronsLeft className="w-3 h-3" />)
          }
        </button>
      )}

      <div className={cn(
        "flex flex-col overflow-hidden h-full",
        !embedded && "bg-white",
        !isMobile && !embedded && "border-e border-gray-100"
      )}>

        {/* Scrollable nav area */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className={cn("py-2", isCollapsed ? "px-1.5" : "px-2")}>

            {/* Browse section */}
            <SectionLabel label={t("browse") || "Browse"} />
            <div className="space-y-0.5 mt-0.5">
              <NavItem
                id={null}
                icon={<Grid3x3 className="w-4 h-4" />}
                label={t("all_activities")}
                count={totalCount}
              />
              <NavItem
                id="created"
                icon={<PenLine className="w-4 h-4" strokeWidth={1.5} />}
                label={t("created_by_me")}
                count={createdCount}
              />
              <NavItem
                id="library"
                icon={<BookOpen className="w-4 h-4" />}
                label={t("my_library")}
                count={libraryCount}
              />
            </div>

            {/* Filter section */}
            <CollapsedDivider />
            <SectionLabel label={t("filter") || "Filter"} />
            <div className="space-y-0.5 mt-0.5">
              <NavItem
                id="favorites"
                icon={<Star className="w-4 h-4" />}
                label={t("favorites")}
                count={favoritesCount}
              />
              <NavItem
                id="in-progress"
                icon={<Play className="w-4 h-4" />}
                label={t("in_progress")}
                count={inProgressCount}
                activeClass="bg-green-50 text-green-700"
                inactiveClass="hover:bg-gray-100 text-green-600"
                countClassName="text-green-500"
              />
            </div>

            {/* Collections */}
            {collections.length > 0 && (
              <>
                <CollapsedDivider />
                <SectionLabel label={t("collections") || "Collections"} />
                <div className="space-y-0.5 mt-0.5">
                  {collections.map((collection) => (
                    isCollapsed ? (
                      <Tooltip key={collection._id}>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => onFolderSelect(`collection-${collection._id}`)}
                            className={cn(
                              "w-full flex items-center justify-center h-9 rounded-lg transition-colors duration-150",
                              selectedFolder === `collection-${collection._id}`
                                ? "bg-primary/10 text-primary"
                                : "hover:bg-gray-100 text-gray-600"
                            )}
                          >
                            <div className="w-5 h-5 rounded flex items-center justify-center bg-primary/80">
                              {selectedFolder === `collection-${collection._id}` ? (
                                <FolderOpen className="w-3.5 h-3.5 text-white" />
                              ) : (
                                <Folder className="w-3.5 h-3.5 text-white" />
                              )}
                            </div>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side={tooltipSide}>
                          {collection.name} ({collection.workflowCount})
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <button
                        key={collection._id}
                        onClick={() => onFolderSelect(`collection-${collection._id}`)}
                        className={cn(
                          "w-full flex items-center gap-2.5 px-3 py-1.5 rounded-lg transition-colors duration-150 text-sm",
                          selectedFolder === `collection-${collection._id}`
                            ? "bg-primary/10 text-primary font-medium"
                            : "hover:bg-gray-100 text-gray-600"
                        )}
                      >
                        <div className="w-5 h-5 rounded flex items-center justify-center bg-primary/80 shrink-0">
                          {selectedFolder === `collection-${collection._id}` ? (
                            <FolderOpen className="w-3.5 h-3.5 text-white" />
                          ) : (
                            <Folder className="w-3.5 h-3.5 text-white" />
                          )}
                        </div>
                        <span className="flex-1 text-start truncate">{collection.name}</span>
                        <span className="text-xs tabular-nums text-gray-400">{collection.workflowCount}</span>
                      </button>
                    )
                  ))}
                </div>
              </>
            )}

            {/* User Folders */}
            <CollapsedDivider />
            <SectionLabel label={t("folders")} />
            {folders.length > 0 && (
              <div className="space-y-0.5 mt-0.5">
                {folders.map((folder) => (
                  isCollapsed ? (
                    <Tooltip key={folder._id}>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => onFolderSelect(folder._id)}
                          className={cn(
                            "w-full flex items-center justify-center h-9 rounded-lg transition-colors duration-150",
                            selectedFolder === folder._id
                              ? "bg-primary/10 text-primary"
                              : "hover:bg-gray-100 text-gray-600"
                          )}
                        >
                          <div
                            className="w-5 h-5 rounded flex items-center justify-center"
                            style={{ backgroundColor: folder.color }}
                          >
                            {selectedFolder === folder._id ? (
                              <FolderOpen className="w-3.5 h-3.5 text-white" />
                            ) : (
                              <Folder className="w-3.5 h-3.5 text-white" />
                            )}
                          </div>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side={tooltipSide}>
                        {folder.name} ({folder.count || 0})
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <div key={folder._id} className="group/folder relative">
                      <button
                        onClick={() => onFolderSelect(folder._id)}
                        className={cn(
                          "w-full flex items-center gap-2.5 px-3 py-1.5 rounded-lg transition-colors duration-150 text-sm",
                          selectedFolder === folder._id
                            ? "bg-primary/10 text-primary font-medium"
                            : "hover:bg-gray-100 text-gray-600"
                        )}
                      >
                        <div
                          className="w-5 h-5 rounded flex items-center justify-center shrink-0"
                          style={{ backgroundColor: folder.color }}
                        >
                          {selectedFolder === folder._id ? (
                            <FolderOpen className="w-3.5 h-3.5 text-white" />
                          ) : (
                            <Folder className="w-3.5 h-3.5 text-white" />
                          )}
                        </div>
                        <span className="flex-1 text-start truncate">{folder.name}</span>
                        <span className="text-xs tabular-nums text-gray-400 group-hover/folder:opacity-0 transition-opacity">
                          {folder.count || 0}
                        </span>
                      </button>

                      {/* Folder Actions — revealed on row hover */}
                      <div className={cn(
                        "absolute top-1/2 -translate-y-1/2",
                        isRTL ? "left-2" : "right-2",
                        "opacity-0 group-hover/folder:opacity-100 transition-opacity"
                      )}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreVertical className="w-3 h-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditDialog(folder);
                              }}
                            >
                              <Edit2 className="w-4 h-4 mr-2" />
                              {t("rename")}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm(t("confirm_delete_folder"))) {
                                  onDeleteFolder(folder._id);
                                }
                              }}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              {t("delete")}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  )
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Pinned footer — Create Folder */}
        <div className={cn(
          "shrink-0 border-t border-gray-100",
          isCollapsed ? "flex justify-center p-2" : "p-3"
        )}>
          {isCollapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setShowCreateDialog(true)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-primary hover:bg-primary/5 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side={tooltipSide}>
                {t("new_folder")}
              </TooltipContent>
            </Tooltip>
          ) : (
            <button
              onClick={() => setShowCreateDialog(true)}
              className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-gray-500 hover:text-primary hover:bg-primary/5 transition-colors duration-150 border border-dashed border-gray-200 hover:border-primary/30"
            >
              <Plus className="w-3.5 h-3.5 shrink-0" />
              <span>{t("new_folder")}</span>
            </button>
          )}
        </div>

      </div>

      {/* Create Folder Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("create_new_folder")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                {t("folder_name")}
              </label>
              <Input
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                placeholder={t("enter_folder_name")}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCreateFolder();
                }}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                {t("color")}
              </label>
              <div className="flex gap-2 flex-wrap">
                {FOLDER_COLORS.map((color) => (
                  <button
                    key={color.hex}
                    onClick={() => {
                      setSelectedColor(color.hex);
                      setShowCustomColorPicker(false);
                      setCustomColor("");
                    }}
                    className={`w-8 h-8 rounded-full transition-all ${
                      selectedColor === color.hex && !showCustomColorPicker
                        ? "ring-2 ring-offset-2 ring-gray-400 scale-110"
                        : "hover:scale-105"
                    }`}
                    style={{ backgroundColor: color.hex }}
                    title={color.name}
                  />
                ))}
                {/* Custom Color Picker Button */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      const input = document.getElementById('create-custom-color-input') as HTMLInputElement;
                      input?.click();
                    }}
                    className={`w-8 h-8 rounded-full transition-all flex items-center justify-center ${
                      showCustomColorPicker
                        ? "ring-2 ring-offset-2 ring-gray-400 scale-110"
                        : "hover:scale-105"
                    }`}
                    style={{
                      background: 'conic-gradient(from 0deg, red, yellow, lime, aqua, blue, magenta, red)',
                    }}
                    title={t("choose_custom_color") || "Choose custom color"}
                  >
                    <Plus className="w-4 h-4 text-white drop-shadow" />
                  </button>
                  <input
                    id="create-custom-color-input"
                    type="color"
                    value={customColor || selectedColor}
                    onChange={(e) => {
                      handleCustomColorChange(e.target.value);
                      setShowCustomColorPicker(true);
                    }}
                    className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateDialog(false);
                setFolderName("");
              }}
            >
              {t("cancel")}
            </Button>
            <Button onClick={handleCreateFolder} disabled={!folderName.trim()}>
              {t("create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Folder Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("edit_folder")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                {t("folder_name")}
              </label>
              <Input
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                placeholder={t("enter_folder_name")}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleEditFolder();
                }}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                {t("color")}
              </label>
              <div className="flex gap-2 flex-wrap">
                {FOLDER_COLORS.map((color) => (
                  <button
                    key={color.hex}
                    onClick={() => {
                      setSelectedColor(color.hex);
                      setShowCustomColorPicker(false);
                      setCustomColor("");
                    }}
                    className={`w-8 h-8 rounded-full transition-all ${
                      selectedColor === color.hex && !showCustomColorPicker
                        ? "ring-2 ring-offset-2 ring-gray-400 scale-110"
                        : "hover:scale-105"
                    }`}
                    style={{ backgroundColor: color.hex }}
                    title={color.name}
                  />
                ))}
                {/* Custom Color Picker Button */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      const input = document.getElementById('edit-custom-color-input') as HTMLInputElement;
                      input?.click();
                    }}
                    className={`w-8 h-8 rounded-full transition-all flex items-center justify-center ${
                      showCustomColorPicker
                        ? "ring-2 ring-offset-2 ring-gray-400 scale-110"
                        : "hover:scale-105"
                    }`}
                    style={{
                      background: 'conic-gradient(from 0deg, red, yellow, lime, aqua, blue, magenta, red)',
                    }}
                    title={t("choose_custom_color") || "Choose custom color"}
                  >
                    <Plus className="w-4 h-4 text-white drop-shadow" />
                  </button>
                  <input
                    id="edit-custom-color-input"
                    type="color"
                    value={customColor || selectedColor}
                    onChange={(e) => {
                      handleCustomColorChange(e.target.value);
                      setShowCustomColorPicker(true);
                    }}
                    className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowEditDialog(false);
                setEditingFolder(null);
                setFolderName("");
              }}
            >
              {t("cancel")}
            </Button>
            <Button onClick={handleEditFolder} disabled={!folderName.trim()}>
              {t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FolderSidebar;
